import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import ArticleContent from "../components/blog/ArticleContent";
import ArticleCard from "../components/blog/ArticleCard";
import BlogSlider from "../components/blog/slider";
import { blogArticles, blogCategories } from "../data/blogContent";

const BlogArticle = () => {
  const { category: categorySlug, slug } = useParams();
  const category = blogCategories[categorySlug];

  const article = useMemo(
    () =>
      blogArticles.find(
        (item) => item.slug === slug && item.category === categorySlug
      ),
    [categorySlug, slug]
  );

  const relatedArticles = useMemo(
    () =>
      blogArticles
        .filter(
          (item) =>
            item.category === categorySlug && item.slug !== slug
        )
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
        .slice(0, 3),
    [categorySlug, slug]
  );

  if (!article || !category) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 bg-[#f7fafc] px-6 text-center">
        <h1 className="text-3xl font-bold text-[#123070]">
          No encontramos este artículo
        </h1>
        <p className="text-gray-600 max-w-md">
          Es posible que el artículo haya sido movido o actualizado. Regresa a
          la categoría para explorar contenidos recientes.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition-colors duration-150"
          >
            Ir al blog
          </Link>
          <Link
            to={`/blog/${categorySlug ?? ""}`}
            className="inline-flex items-center gap-2 px-5 py-3 border border-blue-200 text-blue-700 font-semibold rounded-full hover:border-blue-400 transition-colors duration-150"
          >
            Ver categoría
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <header className="relative">
        <div
          className="h-[340px] sm:h-[420px] bg-cover bg-center"
          style={{ backgroundImage: `url(${category.heroImage})` }}
        >
          <div className="h-full w-full bg-[#0f1a49]/80">
            <div className="max-w-4xl mx-auto h-full flex flex-col justify-center px-6 sm:px-10 lg:px-12 text-white">
              <nav className="flex items-center gap-2 text-sm text-white/70 mb-6">
                <Link to="/blog" className="hover:text-white font-medium">
                  Blog
                </Link>
                <span>/</span>
                <Link
                  to={`/blog/${category.slug}`}
                  className="hover:text-white font-medium"
                >
                  {category.title}
                </Link>
                <span>/</span>
                <span className="font-semibold text-white">
                  {article.title}
                </span>
              </nav>
              <h1 className="text-4xl sm:text-5xl font-bold leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-lg text-white/80 max-w-3xl">
                {article.excerpt}
              </p>
            </div>
          </div>
        </div>
      </header>
      <ArticleContent article={article} />
      {relatedArticles.length > 0 && (
        <section className="bg-[#f4f9fb] py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-10">
              <div>
                <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide">
                  También te puede interesar
                </p>
                <h2 className="text-3xl font-bold text-[#123070] mt-2">
                  Más artículos en {category.title}
                </h2>
              </div>
              <Link
                to={`/blog/${category.slug}`}
                className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-900 transition-colors duration-150"
              >
                Ver todos
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {relatedArticles.map((related) => (
                <ArticleCard key={related.slug} article={related} />
              ))}
            </div>
          </div>
        </section>
      )}
      <BlogSlider />
    </>
  );
};

export default BlogArticle;
